import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import CountUp from "react-countup";
import {
  Train, MapPin, MessageSquareText, Sparkles, ShieldCheck, Clock, Luggage,
  IndianRupee, ChevronDown, Users, CalendarDays, Search, ChevronRight,
} from "lucide-react";
import { waLink } from "../lib/wa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATION_HERO = "https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=1600&h=900&fit=crop&auto=format&q=75";

const STRIP_IMAGES = [
  { url: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&h=500&fit=crop&auto=format&q=70", caption: "Family boarding safely" },
  { url: "https://images.unsplash.com/photo-1541696490-8744a5dc0228?w=800&h=500&fit=crop&auto=format&q=70", caption: "Coolie carrying luggage" },
  { url: "https://images.unsplash.com/photo-1516466723877-e4ec1d736c8a?w=800&h=500&fit=crop&auto=format&q=70", caption: "Platform navigation help" },
  { url: "https://images.unsplash.com/photo-1590059390142-a56a05a75c4c?w=800&h=500&fit=crop&auto=format&q=70", caption: "Heavy bags, easy transfer" },
  { url: "https://images.unsplash.com/photo-1547595925-3f36f93f9c9c?w=800&h=500&fit=crop&auto=format&q=70", caption: "Senior boarding assist" },
];

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function AnimatedScene() {
  return (
    <div className="relative h-40 md:h-56 rounded-3xl overflow-hidden"
         style={{ background: "linear-gradient(135deg, #FFECD1 0%, #FFD9B0 50%, #FFC199 100%)" }}>
      {/* Train — right to left loop */}
      <motion.div
        aria-hidden
        initial={{ x: "110%" }}
        animate={{ x: "-30%" }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        className="absolute top-6 md:top-10"
        style={{ mixBlendMode: "multiply" }}
      >
        <Train className="w-24 h-24 md:w-36 md:h-36 text-neutral-800" strokeWidth={1.4} />
      </motion.div>
      {/* Coolie — walking toward passenger */}
      <motion.div
        aria-hidden
        initial={{ x: "-20%" }}
        animate={{ x: "40%" }}
        transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        className="absolute bottom-4 md:bottom-6"
        style={{ mixBlendMode: "multiply" }}
      >
        <Luggage className="w-16 h-16 md:w-24 md:h-24 text-orange-800" strokeWidth={1.5} />
      </motion.div>
      {/* Passenger silhouette */}
      <div className="absolute bottom-4 md:bottom-6 right-6" style={{ mixBlendMode: "multiply" }}>
        <Users className="w-16 h-16 md:w-24 md:h-24 text-neutral-800" strokeWidth={1.4} />
      </div>
    </div>
  );
}

function Counter({ end, suffix, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  return (
    <div ref={ref} className="rounded-2xl border border-black/10 bg-white p-5 text-center card-lift">
      <div className="font-display text-4xl md:text-5xl" style={{ color: "var(--primary)" }}>
        {inView ? <CountUp end={end} duration={1.6} suffix={suffix} /> : "0"}
      </div>
      <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function ImageMarquee() {
  return (
    <div className="mt-8 relative overflow-hidden">
      <motion.div
        className="flex gap-4 min-w-max"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
      >
        {[...STRIP_IMAGES, ...STRIP_IMAGES].map((img, i) => (
          <div key={i} className="w-64 md:w-80 shrink-0 group">
            <div className="rounded-2xl overflow-hidden aspect-[16/10] border border-black/10">
              <img
                src={img.url}
                alt={img.caption}
                loading="lazy"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />
            </div>
            <div className="mt-2 text-xs text-neutral-500 text-center">{img.caption}</div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    on_time: "bg-emerald-100 text-emerald-800 border-emerald-200",
    late: "bg-amber-100 text-amber-800 border-amber-200",
    cancelled: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`text-[10px] font-bold rounded-full border px-2 py-0.5 uppercase tracking-wider ${map[status.code] || "bg-neutral-100"}`}>
      {status.label}
    </span>
  );
}

function TrainCard({ train, onBook, onCompanion }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border-2 border-black/10 bg-white p-4 md:p-5 flex flex-col gap-3 card-lift"
      data-testid={`train-card-${train.number}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">#{train.number}</div>
          <div className="font-display text-xl leading-tight truncate">{train.name}</div>
          <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {train.origin} · <span className="text-neutral-400">{train.state}</span>
          </div>
        </div>
        <StatusBadge status={train.status} />
      </div>
      <div className="grid grid-cols-3 text-sm">
        <div>
          <div className="text-[10px] uppercase text-neutral-500">Arr Thane</div>
          <div className="font-bold">{train.arr}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-neutral-500">Dep Thane</div>
          <div className="font-bold">{train.dep}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-neutral-500">Platform</div>
          <div className="font-bold">{train.platform}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {days.map((d) => (
          <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
            train.days.includes(d) ? "border-emerald-300 bg-emerald-50 text-emerald-700 font-bold" : "border-black/10 text-neutral-400"
          }`}>{d}</span>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs text-neutral-600">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-emerald-500" /> {train.companions_available} companion{train.companions_available === 1 ? "" : "s"} available
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <button
          data-testid={`train-book-${train.number}`}
          onClick={() => onBook(train)}
          disabled={!train.booking_open}
          className="btn-primary text-xs py-2 px-3 inline-flex items-center justify-center gap-1 disabled:opacity-40"
        >
          <Sparkles className="w-3.5 h-3.5" /> Book Now
        </button>
        <button
          data-testid={`train-companion-${train.number}`}
          onClick={() => onCompanion(train)}
          className="text-xs font-bold inline-flex items-center justify-center gap-1 rounded-full border-2 border-[color:var(--primary)] py-2 px-3 hover:bg-blue-50"
          style={{ color: "var(--primary)" }}
        >
          <Users className="w-3.5 h-3.5" /> Find a Companion
        </button>
      </div>
    </motion.div>
  );
}

export default function RailLuggageAssist() {
  const nav = useNavigate();
  const boardRef = useRef(null);
  const [tab, setTab] = useState("today"); // today | tomorrow | month
  const [date, setDate] = useState(todayISO());
  const [origin, setOrigin] = useState("");
  const [origins, setOrigins] = useState([]);
  const [trains, setTrains] = useState([]);
  const [month, setMonth] = useState([]);

  useEffect(() => {
    fetch(`${API}/rail/origins`).then((r) => r.json()).then((d) => setOrigins(d.origins || []));
  }, []);

  useEffect(() => {
    const p = new URLSearchParams({ date });
    if (origin) p.set("origin", origin);
    fetch(`${API}/rail/trains?${p}`).then((r) => r.json()).then((d) => setTrains(d.trains || []));
  }, [date, origin]);

  useEffect(() => {
    if (tab !== "month") return;
    fetch(`${API}/rail/month`).then((r) => r.json()).then((d) => setMonth(d.days || []));
  }, [tab]);

  const pickTab = (t) => {
    setTab(t);
    if (t === "today") setDate(todayISO());
    else if (t === "tomorrow") setDate(todayISO(1));
  };

  const scrollToBoard = () => boardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const bookTrain = (train) => {
    const params = new URLSearchParams({
      pickup: "Thane Station",
      landmark: `Train ${train.number} · ${train.name} · ${train.arr}`,
    });
    nav(`/services/railway?${params.toString()}`);
  };
  const findCompanion = (train) => {
    window.open(
      waLink(`Hi HeylpMate! I want a *travel companion* on train *${train.number} ${train.name}* (${train.origin} → Thane, ${train.date}). Please help.`),
      "_blank",
    );
  };

  return (
    <div>
      {/* HERO */}
      <div className="relative overflow-hidden">
        <img src={STATION_HERO} alt="Thane Station" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.7), rgba(29,78,216,0.85))" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20 text-white">
          <div className="chip bg-white/95 text-neutral-900 border-white/40 mb-5">
            <Luggage className="w-3.5 h-3.5" /> Rail Luggage Assist · inside HeylpMate
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.02]">
                Struggling with luggage <br /> at <span className="italic" style={{ color: "#FFB86B" }}>Thane Station?</span>
              </h1>
              <p className="mt-4 text-white/90 max-w-lg">
                Pre-book a verified coolie by train number. Meet you at the platform, load your bags, senior/child boarding — done.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  data-testid="prebook-cta"
                  onClick={scrollToBoard}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3 shadow-lg transition-all"
                >
                  <Sparkles className="w-4 h-4" /> Pre-Book Your Coolie Now
                </button>
                <a
                  href={waLink("Hi HeylpMate! I need luggage help at Thane Station. Please help.")}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 font-bold px-6 py-3 hover:bg-white/10 transition-colors"
                >
                  <MessageSquareText className="w-4 h-4" /> WhatsApp us
                </a>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="chip bg-white/15 border-white/30 text-white"><ShieldCheck className="w-3.5 h-3.5" /> ID-verified porters</span>
                <span className="chip bg-white/15 border-white/30 text-white"><Clock className="w-3.5 h-3.5" /> Match in ~10 min</span>
                <span className="chip bg-white/15 border-white/30 text-white"><IndianRupee className="w-3.5 h-3.5" /> ₹399 per task</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <AnimatedScene />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {/* Counters */}
        <div className="grid grid-cols-2 gap-4 max-w-md" data-testid="rail-counters">
          <Counter end={100} suffix="+" label="Passengers helped" />
          <Counter end={500} suffix="+" label="Coolie members" />
        </div>

        {/* Marquee strip */}
        <ImageMarquee />

        {/* Board */}
        <div ref={boardRef} className="mt-14">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="label-eyebrow">train discovery board</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Trains halting at Thane</h2>
            </div>
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5" /> Showing <b>{date}</b>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-5 flex gap-2" data-testid="rail-tabs">
            {[{ id: "today", label: "Today" }, { id: "tomorrow", label: "Tomorrow" }, { id: "month", label: "Full Month" }].map((t) => (
              <button
                key={t.id}
                data-testid={`rail-tab-${t.id}`}
                onClick={() => pickTab(t.id)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${tab === t.id ? "text-white shadow-md" : "border-2 border-black/10 bg-white text-neutral-700"}`}
                style={tab === t.id ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Full-month calendar row */}
          {tab === "month" && (
            <div className="mt-4 -mx-2 px-2 overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
              <div className="flex gap-2 min-w-max pb-2" data-testid="rail-month-strip">
                {month.map((d) => {
                  const active = d.date === date;
                  return (
                    <button
                      key={d.date}
                      data-testid={`rail-day-${d.date}`}
                      onClick={() => setDate(d.date)}
                      className={`shrink-0 w-16 rounded-2xl border-2 py-2 text-center transition-all ${
                        active ? "text-white shadow-md" : "border-black/10 bg-white text-neutral-700 hover:border-black/30"
                      }`}
                      style={active ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
                    >
                      <div className="text-[10px] uppercase tracking-widest">{d.day}</div>
                      <div className="font-bold text-lg">{d.date.slice(-2)}</div>
                      <div className={`text-[9px] ${active ? "text-white/80" : "text-neutral-500"}`}>{d.count} trains</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Origin filter */}
          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Travelling from</label>
            <div className="mt-1.5 relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="rail-origin-filter"
                list="rail-origins-list"
                className="flex h-10 w-full rounded-full border border-black/15 bg-white pl-9 pr-3 text-sm"
                placeholder="e.g. Pune Junction, Delhi (NDLS), Bengaluru"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
              />
              <datalist id="rail-origins-list">
                {origins.map((o) => <option key={o} value={o} />)}
              </datalist>
            </div>
          </div>

          {/* Train cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="rail-trains-grid">
            {trains.map((t) => (
              <TrainCard key={`${t.number}-${t.date}`} train={t} onBook={bookTrain} onCompanion={findCompanion} />
            ))}
            {trains.length === 0 && (
              <div className="col-span-full text-center py-10 text-neutral-500 text-sm">
                No trains match. Try clearing the origin or picking a different date.
              </div>
            )}
          </div>
        </div>

        {/* Reassurance */}
        <div className="mt-14 rounded-3xl p-6 md:p-8 text-white" style={{ background: "linear-gradient(135deg, #10B981, #00604A)" }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/80">one platform · not a separate app</div>
              <h3 className="font-display text-2xl md:text-3xl font-bold mt-1">Rail Luggage Assist lives inside HeylpMate.</h3>
              <p className="text-white/90 mt-1 text-sm md:text-base">Same login, same UPI, same helpers, same admin. One family.</p>
            </div>
            <Link to="/services" data-testid="rail-all-services" className="inline-flex items-center gap-2 rounded-full bg-white text-emerald-700 font-bold px-5 py-2.5">
              See all services <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
